// Shared Navbar component used across all portal pages
// Shows the DriverWatch logo, navigation buttons, and user profile avatar dropdown

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import DeviceSearchPalette from "./DeviceSearchPalette";

const API_URL = config.API_URL;

export default function Navbar({ orgName, pendingRequests = 0 }) {
  const [deviceEvents, setDeviceEvents] = useState(null);
  const [triggerWatchEnabled, setTriggerWatchEnabled] = useState(false);
  const [searchPaletteOpen, setSearchPaletteOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false);
  const eventsRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalMenuRef = useRef(null);
  const appSwitcherRef = useRef(null);
  const navigate = useNavigate();
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  function getInitials() {
    if (storedUser.full_name) {
      const parts = storedUser.full_name.split(" ");
      return parts.map(p => p[0]).join("").toUpperCase().slice(0, 2);
    }
    return storedUser.email ? storedUser.email[0].toUpperCase() : "?";
  }

  // Check whether TriggerWatch (Tool 2) is enabled for this specific org (falls back to global default)
  useEffect(() => {
    if (!ORG_ID) return;
    axios.get(`${API_URL}/platform-settings/triggerwatch_enabled?org_id=${ORG_ID}`)
      .then(res => setTriggerWatchEnabled(res.data.value === "true"))
      .catch(() => setTriggerWatchEnabled(false));
  }, [ORG_ID]);

  // Cmd+K (Mac) or Ctrl+K (Windows) opens the device search palette from anywhere
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchPaletteOpen(true);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (portalMenuRef.current && !portalMenuRef.current.contains(e.target)) setPortalMenuOpen(false);
      if (appSwitcherRef.current && !appSwitcherRef.current.contains(e.target)) setAppSwitcherOpen(false);
      if (eventsRef.current && !eventsRef.current.contains(e.target)) setEventsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSignOut() {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    window.location.href = "/login";
  }

  return (
    <nav className="bg-gray-900 border-b border-gray-800 px-8 py-4 flex items-center justify-between">

      {/* Logo */}
      <div
        onClick={() => navigate("/")}
        className="flex items-center gap-3 cursor-pointer"
      >
        <img src="/logo-symbol.svg" alt="AleroSys" className="h-8 w-8" />
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-lg hover:text-blue-400 transition-colors">AleroSys</span>
          <span className="text-gray-600 text-lg font-light">—</span>
          <span className="text-blue-500 text-lg font-semibold">DriverWatch</span>
        </div>
      </div>

      {/* Right side navigation */}
      <div className="flex items-center gap-4">

        {/* Device search — opens the search palette */}
        <button
          onClick={() => setSearchPaletteOpen(true)}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1.5 rounded-lg transition-colors"
          title="Search devices (Cmd+K)"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <kbd className="hidden sm:inline text-gray-600 text-xs border border-gray-700 rounded px-1">⌘K</kbd>
        </button>

        {orgName && <span className="text-gray-400 text-sm">{orgName}</span>}

        {/* Send Alert button — only show on dashboard */}
        {window.location.pathname === "/" && (
          <button
            onClick={async () => {
              try {
                const response = await axios.post(`${API_URL}/organizations/${ORG_ID}/send-alert`);
                alert(`✅ ${response.data.message} — ${response.data.devices_alerted} device(s) included`);
              } catch (err) {
                alert("❌ Failed to send alert. Please try again.");
              }
            }}
            className="bg-orange-700 hover:bg-orange-600 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            Send Alert
          </button>
        )}

        {/* Back button — context-aware: returns to whichever section's home the user is currently in */}
        {(() => {
          const inTriggerWatch = window.location.pathname.startsWith("/triggerwatch");
          const isSectionHome = window.location.pathname === "/" || window.location.pathname === "/triggerwatch";

          if (isSectionHome) return null;

          return (
            <button
              onClick={() => navigate(inTriggerWatch ? "/triggerwatch" : "/")}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {inTriggerWatch ? "← TriggerWatch" : "← Dashboard"}
            </button>
          );
        })()}

        {/* App switcher — house icon */}
        <div className="relative" ref={appSwitcherRef}>
          <button
            onClick={() => setAppSwitcherOpen(!appSwitcherOpen)}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            title="Switch app"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>

          {appSwitcherOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50 p-3">
              <p className="text-gray-500 text-xs px-2 mb-2">Your apps</p>
              <div
                onClick={() => { navigate("/"); setAppSwitcherOpen(false); }}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-800 border border-blue-800 cursor-pointer mb-2"
              >
                <div className="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <rect x="7" y="7" width="10" height="10" rx="1"/>
                    <path d="M9 7V4M12 7V4M15 7V4M9 20v-3M12 20v-3M15 20v-3M4 9h3M4 12h3M4 15h3M17 9h3M17 12h3M17 15h3"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">DriverWatch</p>
                  <p className="text-gray-400 text-xs">Driver health monitoring</p>
                </div>
                <span className="ml-auto bg-green-900 text-green-300 text-xs px-2 py-0.5 rounded-full">Active</span>
              </div>
              <div
                onClick={() => {
                  if (!triggerWatchEnabled) return;
                  navigate("/triggerwatch");
                  setAppSwitcherOpen(false);
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg mb-2 ${
                  triggerWatchEnabled
                    ? "bg-gray-800 border border-purple-800 cursor-pointer"
                    : "opacity-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${triggerWatchEnabled ? "bg-purple-900" : "bg-gray-800"}`}>
                  <svg className={`w-4 h-4 ${triggerWatchEnabled ? "text-purple-400" : "text-gray-600"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                  </svg>
                </div>
                <div>
                  <p className={`text-sm font-semibold ${triggerWatchEnabled ? "text-white" : "text-gray-300"}`}>TriggerWatch</p>
                  <p className="text-gray-500 text-xs">{triggerWatchEnabled ? "Package deployment" : "Coming soon"}</p>
                </div>
                {triggerWatchEnabled && (
                  <span className="ml-auto bg-purple-900 text-purple-300 text-xs px-2 py-0.5 rounded-full">Active</span>
                )}
              </div>
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg opacity-50">
                <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-semibold">Tool 3</p>
                  <p className="text-gray-500 text-xs">Coming soon</p>
                </div>
              </div>
              <div className="border-t border-gray-800 mt-2 pt-2">
                <button
                  onClick={() => { navigate("/portal"); setAppSwitcherOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-400 hover:text-white text-xs transition-colors"
                >
                  ← Back to portal home
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Device events radar icon */}
        {deviceEvents && deviceEvents.total > 0 && (
          <div className="relative" ref={eventsRef}>
            <button
              onClick={() => setEventsOpen(!eventsOpen)}
              className="relative text-gray-400 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6.343 17.657a8 8 0 1 0 11.314 -11.314a8 8 0 0 0 -11.314 11.314z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 14.828a4 4 0 1 0 5.656 -5.656a4 4 0 0 0 -5.656 5.656z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12l-3 -3" />
              </svg>
              <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {deviceEvents.total}
              </span>
            </button>

            {eventsOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50">
                <div className="px-4 py-3 border-b border-gray-800">
                  <p className="text-white text-sm font-semibold">Device changes (last 24h)</p>
                </div>
                <div className="px-2 py-2 max-h-64 overflow-y-auto">
                  {deviceEvents.added.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg">
                      <span className="text-green-400 text-xs">+</span>
                      <span className="text-gray-300 text-sm">{e.hostname}</span>
                      <span className="text-gray-500 text-xs ml-auto">Added</span>
                    </div>
                  ))}
                  {deviceEvents.removed.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg">
                      <span className="text-red-400 text-xs">−</span>
                      <span className="text-gray-300 text-sm">{e.hostname}</span>
                      <span className="text-gray-500 text-xs ml-auto">Removed</span>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 border-t border-gray-800">
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/organizations/${ORG_ID}/device-events/dismiss`, {}, {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                        });
                        setDeviceEvents(null);
                        setEventsOpen(false);
                      } catch (err) {
                        console.error("Failed to dismiss events", err);
                      }
                    }}
                    className="w-full text-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Dismiss all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Portal dropdown menu */}
        <div className="relative" ref={portalMenuRef}>
          <button
            onClick={() => setPortalMenuOpen(!portalMenuOpen)}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1.5 rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Portal
          </button>

          {portalMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50">
              <div className="px-2 py-2">
                <button
                  onClick={() => { navigate("/users"); setPortalMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Users
                  {pendingRequests > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center ml-auto">
                      {pendingRequests}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { navigate("/deployment"); setPortalMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="16 6 12 2 8 6"/><line strokeLinecap="round" strokeWidth={2} x1="12" y1="2" x2="12" y2="15"/></svg>
                  Deployment
                </button>
                <button
                  onClick={() => { navigate("/scheduled-updates"); setPortalMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2" strokeWidth={2}/><line strokeLinecap="round" strokeWidth={2} x1="16" y1="2" x2="16" y2="6"/><line strokeLinecap="round" strokeWidth={2} x1="8" y1="2" x2="8" y2="6"/><line strokeWidth={2} x1="3" y1="10" x2="21" y2="10"/></svg>
                  Scheduled Updates
                </button>
                {triggerWatchEnabled && (
                  <>
                    <button
                      onClick={() => { navigate("/triggerwatch/packages"); setPortalMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="3.27 6.96 12 12.01 20.73 6.96"/><line strokeWidth={2} x1="12" y1="22.08" x2="12" y2="12"/></svg>
                      TriggerWatch Packages
                    </button>
                    <button
                      onClick={() => { navigate("/triggerwatch/deployments"); setPortalMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                      TriggerWatch Deployments
                    </button>
                    <button
                      onClick={() => { navigate("/triggerwatch/device-groups"); setPortalMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="3" width="7" height="7" rx="1" strokeWidth={2}/><rect x="14" y="3" width="7" height="7" rx="1" strokeWidth={2}/><rect x="14" y="14" width="7" height="7" rx="1" strokeWidth={2}/><rect x="3" y="14" width="7" height="7" rx="1" strokeWidth={2}/></svg>
                      Device Groups
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User avatar dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-9 h-9 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm transition-colors"
          >
            {getInitials()}
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50">
              <div className="px-4 py-3 border-b border-gray-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {getInitials()}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{storedUser.full_name || "User"}</p>
                    <p className="text-gray-400 text-xs">{storedUser.email}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  storedUser.role === "admin"
                    ? "bg-blue-900 text-blue-300"
                    : "bg-gray-700 text-gray-300"
                }`}>
                  {storedUser.role === "admin" ? "Admin" : "Viewer"}
                </span>
              </div>

              <div className="px-2 py-2 border-b border-gray-800">
                <button
                  onClick={() => { navigate("/feature-requests"); setDropdownOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 6.072 0c-.653.653-1.169 1.545-1.169 2.457V19a2 2 0 0 1-2 2h0a2 2 0 0 1-2-2v-.6c0-.912-.516-1.804-1.169-2.457z"/></svg>
                  Feature Requests
                </button>
                <button
                  onClick={() => { navigate("/mfa-setup"); setDropdownOpen(false); }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="5" y="11" width="14" height="10" rx="2" strokeWidth={2}/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                  Two-Factor Authentication
                </button>
              </div>

              <div className="px-2 py-2">
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-3 py-2 text-red-400 hover:text-red-300 hover:bg-gray-800 rounded-lg text-sm transition-colors"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed top-[73px] left-0 right-0 z-50">
        <DeviceSearchPalette isOpen={searchPaletteOpen} onClose={() => setSearchPaletteOpen(false)} />
      </div>
    </nav>
  );
}